import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import "chartjs-adapter-date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
);

const skeletonStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .skeleton-box {
    display: inline-block;
    height: 1em;
    position: relative;
    overflow: hidden;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-repeat: no-repeat;
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
    border-radius: 4px;
    width: 100%;
  }
  .skeleton-circle {
    width: 90px;
    height: 90px;
    border-radius: 10%;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
  }
  .skeleton-chart {
    width: 100%;
    height: 100%;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-repeat: no-repeat;
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
    border-radius: 8px;
  }
  .mobile-menu-container {
    transition: all 0.3s ease;
    overflow: hidden;
  }
`;

const Dashboardaa = () => {
  const [theme, setTheme] = useState("light");
  const [typedText, setTypedText] = useState("");
  const [activeTab, setActiveTab] = useState("compliance");
  const [insightsTab, setInsightsTab] = useState("trend");
  const [revenueFilter, setRevenueFilter] = useState("year");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dateRange, setDateRange] = useState("30d");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [stats, setStats] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [topAgentDetails, setTopAgentDetails] = useState(null);
  const [totalStats, setTotalStats] = useState({ paid: 0, pending: 0 });
  const [revenueTrendData, setRevenueTrendData] = useState([]);
  const [enumerationTrendData, setEnumerationTrendData] = useState([]);

  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(true);

  const typewriterRef = useRef(null);

  const BASE_URL = "https://adera-web-api.azurewebsites.net/v1";
  const REVENUE_TARGET = 1500000000;
  const lines = ["Welcome Back, Admin", "Let's keep Edo's skyline compliant"];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    })
      .format(amount || 0)
      .replace("NGN", "₦");
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        const [statsRes, adminRes] = await Promise.all([
          fetch(`${BASE_URL}/admin/dashboard/stats`, { headers }),
          fetch(`${BASE_URL}/admin`, { headers }),
        ]);

        if (statsRes.ok) {
          const statsJson = await statsRes.json();
          if (statsJson.ok) {
            setStats(statsJson.data);

            if (statsJson.data.charts?.revenue) {
              setRevenueTrendData(statsJson.data.charts.revenue);
            }

            if (statsJson.data.charts?.ad) {
              setEnumerationTrendData(statsJson.data.charts.ad);
            }

            if (statsJson.data.topAgent?.id) {
              fetchAgentDetails(statsJson.data.topAgent.id, headers);
            }
          }
        }

        if (adminRes.ok) {
          const adminJson = await adminRes.json();
          if (adminJson.ok) setAdmin(adminJson.data);
        }

        fetchPayments(headers);
        setChartsLoading(false);
      } catch (error) {
        console.error("Failed to fetch core dashboard data:", error);
        setChartsLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const fetchPayments = async (headers) => {
    try {
      const response = await fetch(`${BASE_URL}/admin/payment?all=true`, {
        headers,
      });
      if (response.ok) {
        const paymentJson = await response.json();
        const allPaymentsData =
          paymentJson.data.pageData || paymentJson.data || [];

        const calculatedStats = allPaymentsData.reduce(
          (acc, curr) => {
            const amount = parseFloat(curr.amount) || 0;

            if (curr.status === "Successful") {
              acc.paid += amount;
            } else if (
              ["Initialize", "AwaitingPayment", "Pending", "Failed"].includes(
                curr.status,
              )
            ) {
              acc.pending += amount;
            }

            return acc;
          },
          { paid: 0, pending: 0 },
        );

        setTotalStats(calculatedStats);
      }
    } catch (error) {
      console.error("Failed to fetch payments:", error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const fetchAgentDetails = async (agentId, headers) => {
    try {
      const response = await fetch(`${BASE_URL}/admin/agent/${agentId}`, {
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        if (data.ok) setTopAgentDetails(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch top agent details");
    }
  };

  useEffect(() => {
    let index = 0;
    let charIndex = 0;
    let isDeleting = false;
    let timer;

    const type = () => {
      const currentLine = lines[index % lines.length];

      if (!isDeleting && charIndex <= currentLine.length) {
        setTypedText(currentLine.substring(0, charIndex) + "|");
        charIndex++;
      } else if (isDeleting && charIndex >= 0) {
        setTypedText(currentLine.substring(0, charIndex) + "|");
        charIndex--;
      }

      if (charIndex > currentLine.length) {
        isDeleting = true;
        timer = setTimeout(type, 2000);
      } else if (isDeleting && charIndex === 0) {
        isDeleting = false;
        index++;
        timer = setTimeout(type, 300);
      } else {
        timer = setTimeout(type, isDeleting ? 50 : 70);
      }
    };

    type();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const themeMode =
      root.getAttribute("data-bs-theme-mode") ||
      localStorage.getItem("data-bs-theme") ||
      "light";
    setTheme(themeMode);
    root.setAttribute("data-bs-theme", themeMode);
  }, []);

  const getDateRangeFilter = () => {
    const now = new Date();
    let startDate = new Date(0);

    switch (dateRange) {
      case "7d":
        startDate = new Date(now.setDate(now.getDate() - 7));
        break;
      case "30d":
        startDate = new Date(now.setDate(now.getDate() - 30));
        break;
      case "90d":
        startDate = new Date(now.setDate(now.getDate() - 90));
        break;
      case "custom":
        if (customStartDate) startDate = new Date(customStartDate);
        break;
      default:
        startDate = new Date(0);
    }

    let endDate = new Date();
    endDate.setHours(23, 59, 59, 999);

    if (dateRange === "custom" && customEndDate) {
      endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999);
    }

    return { startDate, endDate };
  };

  const trendData = useMemo(() => {
    if (!enumerationTrendData.length) return { labels: [], counts: [] };
    const { startDate, endDate } = getDateRangeFilter();

    const filteredData = enumerationTrendData
      .filter((item) => {
        const itemDate = new Date(item.timestamp);
        return itemDate >= startDate && itemDate <= endDate;
      })
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    return {
      labels: filteredData.map((item) => {
        const date = new Date(item.timestamp);
        return date.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        });
      }),
      counts: filteredData.map((item) => item.count),
    };
  }, [enumerationTrendData, dateRange, customStartDate, customEndDate]);

  const processedRevenueTrend = useMemo(() => {
    if (!revenueTrendData.length) return { labels: [], amounts: [] };
    const { startDate, endDate } = getDateRangeFilter();

    const filteredData = revenueTrendData
      .filter((item) => {
        const itemDate = new Date(item.timestamp);
        return itemDate >= startDate && itemDate <= endDate;
      })
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    return {
      labels: filteredData.map((d) => {
        const date = new Date(d.timestamp);
        return date.toLocaleDateString("en-GB", {
          month: "short",
          day: "numeric",
        });
      }),
      amounts: filteredData.map((d) => d.amount || 0),
    };
  }, [revenueTrendData, dateRange, customStartDate, customEndDate]);

  const adTypeDistribution = useMemo(() => {
    if (stats?.charts?.topCategoryType) {
      const flattened = [];
      stats.charts.topCategoryType.forEach((cat) => {
        if (cat.company_types && Array.isArray(cat.company_types)) {
          cat.company_types.forEach((ct) => {
            flattened.push({ type: ct.type, count: ct.count });
          });
        } else {
          flattened.push({ type: cat.type, count: cat.count || 0 });
        }
      });

      return flattened.sort((a, b) => b.count - a.count).slice(0, 10);
    }
    return [];
  }, [stats]);

  const getFilteredCollectedAmount = () => {
    if (!stats?.revenue) return 0;
    switch (revenueFilter) {
      case "today":
        return stats.revenue.today || 0;
      case "week":
        return stats.revenue.week || 0;
      case "month":
        return stats.revenue.month || 0;
      case "year":
        return stats.revenue.year || 0;
      default:
        return stats.revenue.total || 0;
    }
  };

  const currentTotalRevenue = stats?.revenue?.total || 0;
  const revenueTargetPercentage = parseFloat(
    ((currentTotalRevenue / REVENUE_TARGET) * 100).toFixed(2),
  );
  const pendingPaymentsCount = stats?.payment?.stats?.Initialize || 0;
  const paymentTotal = stats?.payment?.total || 0;
  const paymentTarget = 1500000000;
  const paymentPercentage = parseFloat(
    ((paymentTotal / paymentTarget) * 100).toFixed(2),
  );
  const expectedRevenueTotal = stats?.expectedRevenue?.total || 0;
  const expectedRevenueTargetPercentage = parseFloat(
    ((expectedRevenueTotal / REVENUE_TARGET) * 100).toFixed(2),
  );

  const getAgentImage = () => {
    if (stats?.topAgent?.avatar_url) return stats.topAgent.avatar_url;
    return "assets/edsal.png";
  };

  const getAgentName = () => {
    if (stats?.topAgent) {
      return `${stats.topAgent.first_name || ""} ${stats.topAgent.last_name || ""}`.trim();
    }
    return "N/A";
  };

  const getAgentSignedClients = () => {
    return (
      stats?.topAgent?.individuals_signed || stats?.topAgent?.total_clients || 0
    );
  };

  const getAgentTotalAmount = () => {
    return stats?.topAgent?.total_amount_paid || 0;
  };

  const revenueLineChartData = {
    labels: processedRevenueTrend.labels,
    datasets: [
      {
        label: "Revenue Trend",
        data: processedRevenueTrend.amounts,
        borderColor: "#20AA3E",
        backgroundColor: "rgba(32, 170, 62, 0.1)",
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const enumerationLineChartData = {
    labels: trendData.labels,
    datasets: [
      {
        label: "Ads Registered",
        data: trendData.counts,
        borderColor: "#20AA3E",
        backgroundColor: "rgba(32, 170, 62, 0.1)",
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e1e2d",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#20AA3E",
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) label += ": ";
            if (context.parsed.y !== null) {
              if (insightsTab === "revenue-trend")
                return label + formatCurrency(context.parsed.y);
              return label + context.parsed.y;
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45 },
      },
      y: {
        grid: { color: "rgba(0,0,0,0.05)" },
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            if (insightsTab === "revenue-trend")
              return formatCurrency(value).replace(/\.00$/, "");
            return value;
          },
        },
      },
    },
  };

  const barChartData = {
    labels: adTypeDistribution.map((item) => item.type),
    datasets: [
      {
        label: "Active Count",
        data: adTypeDistribution.map((item) => item.count),
        backgroundColor: "#20AA3E",
        borderRadius: 4,
        barThickness: 20,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { color: "rgba(0,0,0,0.05)" }, beginAtZero: true },
      y: { grid: { display: false } },
    },
  };

  return (
    <>
      <style>{skeletonStyles}</style>
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div
          className="app-page flex-column flex-column-fluid"
          id="kt_app_page"
        >
          <div id="kt_app_header" className="app-header">
            <div
              className="app-container container-fluid d-flex align-items-stretch justify-content-between"
              id="kt_app_header_container"
            >
              <div className="d-flex align-items-center d-lg-none ms-n2 me-2">
                <button
                  className="btn btn-icon btn-active-color-primary w-35px h-35px"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  <i className="ki-outline ki-abstract-14 fs-1"></i>
                </button>
              </div>

              <div className="app-header-logo d-flex align-items-center me-lg-20 gap-2">
                <Link to="/">
                  <img
                    alt="Logo"
                    src="assets/aderalogo.png"
                    className="h-60px"
                  />
                </Link>
              </div>

              <div className="d-flex align-items-stretch justify-content-between flex-lg-grow-1">
                <div
                  className="d-flex align-items-stretch"
                  id="kt_app_header_menu_wrapper"
                >
                  <div className="app-header-menu app-header-mobile-drawer align-items-stretch">
                    <div
                      className={`menu menu-rounded menu-column menu-lg-row menu-active-bg menu-title-gray-600 fw-semibold fs-6 align-items-stretch my-5 px-2 
                        ${mobileMenuOpen ? "d-block position-absolute bg-white w-100 start-0 top-100 shadow-sm p-4 z-index-100" : "d-none d-lg-flex"}`}
                      style={mobileMenuOpen ? { zIndex: 999 } : {}}
                    >
                      <div className="menu-item here show menu-here-bg me-2">
                        <Link
                          to="/dashboarda"
                          className="menu-link"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span className="menu-title">Home</span>
                        </Link>
                      </div>
                      <div className="menu-item me-2">
                        <Link
                          to="/ad-management"
                          className="menu-link"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span className="menu-title">Advertisement</span>
                        </Link>
                      </div>
                      <div className="menu-item me-2">
                        <Link
                          to="/agents"
                          className="menu-link"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span className="menu-title">Agents</span>
                        </Link>
                      </div>
                      <div className="menu-item me-2">
                        <Link
                          to="/payments"
                          className="menu-link"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span className="menu-title">Payments</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="app-navbar flex-shrink-0 d-flex align-items-center gap-3">
                  <div className="text-end d-none d-sm-flex flex-column justify-content-center me-3">
                    <span className="text-gray-500 fs-8 fw-bold">Hello</span>
                    <span className="text-gray-800 fs-7 fw-bold">
                      EDSAA Admin
                    </span>
                  </div>
                  <div className="cursor-pointer symbol symbol-circle symbol-40px">
                    <img src="assets/edsal.png" alt="User" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="app-wrapper flex-column flex-column-fluid"
            id="kt_app_wrapper"
          >
            <div className="app-container container-fluid">
              <div
                className="app-main flex-column flex-row-fluid"
                id="kt_app_main"
              >
                <div className="d-flex flex-column flex-column-fluid">
                  <div
                    id="kt_app_content"
                    className="app-content flex-column-fluid pt-10"
                  >
                    <div className="row mb-5">
                      <div className="col-lg-5">
                        <div className="d-flex flex-column justify-content-center text-center text-lg-start mb-5">
                          <h1
                            className="fs-2x fw-bold mb-5"
                            style={{
                              letterSpacing: "-0.5px",
                              minHeight: "60px",
                            }}
                          >
                            <span ref={typewriterRef}>{typedText}</span>
                          </h1>
                          <div className="fs-4 text-muted mb-7">
                            Quick overview of revenue, permits, compliance, and
                            operations for today.
                          </div>
                          <Link
                            to="/ad-management"
                            className="btn btn-success fw-semibold px-6 py-3 align-self-start mx-auto mx-lg-0"
                          >
                            <i className="ki-outline ki-eye fs-2 me-2"></i>
                            View Advertisements
                          </Link>
                        </div>
                      </div>

                      <div className="col-lg-7">
                        <div className="row g-5">
                          <div className="col-xl-4">
                            <div className="card bg-dark hoverable shadow-sm text-decoration-none h-100">
                              <div className="card-body p-6">
                                <div className="d-flex align-items-center gap-3 mb-6">
                                  <i className="ki-duotone ki-verify text-white fs-2x">
                                    <span className="path1"></span>
                                    <span className="path2"></span>
                                    <span className="path3"></span>
                                    <span className="path4"></span>
                                    <span className="path5"></span>
                                  </i>
                                  <div className="fw-bold text-gray-100">
                                    Demand Notice
                                  </div>
                                </div>
                                <div className="mb-2">
                                  <div className="fs-2x fw-bold text-white">
                                    {loading ? (
                                      <div className="skeleton-box w-50 opacity-50"></div>
                                    ) : (
                                      stats?.ad?.total
                                    )}
                                  </div>
                                  <div className="text-gray-500 fs-7">
                                    Total Notice Created
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-xl-4">
                            <div className="card bg-white hoverable shadow-sm text-decoration-none h-100">
                              <div className="card-body p-6">
                                <div className="d-flex align-items-center gap-3 mb-6">
                                  <i className="ki-duotone ki-bill text-primary fs-2x">
                                    <span className="path1"></span>
                                    <span className="path2"></span>
                                    <span className="path3"></span>
                                    <span className="path4"></span>
                                    <span className="path6"></span>
                                    <span className="path7"></span>
                                    <span className="path8"></span>
                                    <span className="path9"></span>
                                    <span className="path10"></span>
                                  </i>
                                  <div className="fw-bold text-gray-900">
                                    Total Revenue
                                  </div>
                                </div>
                                <div className="mb-2">
                                  <div className="fs-2x fw-bold text-gray-900">
                                    {loading ? (
                                      <div className="skeleton-box w-50"></div>
                                    ) : (
                                      formatCurrency(stats?.revenue?.total)
                                    )}
                                  </div>
                                  <div className="text-muted fs-7">
                                    Successful Payments
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-xl-4">
                            <div className="card bg-warning hoverable shadow-sm text-decoration-none h-100">
                              <div className="card-body p-6">
                                <div className="d-flex align-items-center gap-3 mb-6">
                                  <i className="ki-duotone ki-chart-simple text-white fs-2x">
                                    <span className="path1"></span>
                                    <span className="path2"></span>
                                    <span className="path3"></span>
                                    <span className="path4"></span>
                                  </i>
                                  <div className="fw-bold text-white">
                                    Expected Revenue
                                  </div>
                                </div>
                                <div className="mb-2">
                                  <div className="fs-2x fw-bold text-white">
                                    {loading ? (
                                      <div className="skeleton-box w-50 opacity-50"></div>
                                    ) : (
                                      formatCurrency(
                                        stats?.expectedRevenue?.total,
                                      )
                                    )}
                                  </div>
                                  <div className="text-white opacity-75 fs-7">
                                    Outstanding Balance
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className="card-rounded shadow p-6 mb-10"
                      style={{
                        background:
                          "linear-gradient(90deg, #20AA3E 0%, #03A588 100%)",
                        border: "none",
                      }}
                    >
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                        <div className="mb-4 mb-md-0">
                          <div className="fs-2x fw-bold text-white mb-2">
                            You have{" "}
                            <Link
                              to="/payments"
                              className="text-white text-decoration-underline"
                            >
                              <b>
                                {loading ? "..." : pendingPaymentsCount}{" "}
                                Payments
                              </b>{" "}
                              Pending
                            </Link>
                          </div>
                          <div className="fs-5 text-white opacity-75">
                            {loading
                              ? "Loading..."
                              : `There are ${stats?.ad?.today || 0} ads created today`}
                          </div>
                        </div>
                        <Link
                          to="/payments"
                          className="btn btn-lg btn-light fw-semibold px-6 d-inline-flex align-items-center"
                        >
                          <i className="ki-outline ki-wallet fs-2 me-2"></i>
                          View Pending Advertisements
                        </Link>
                      </div>
                    </div>

                    <div className="row flex-row-fluid">
                      <div className="col-lg-5">
                        <div
                          className="card card-flush h-xl-100 mb-l-10"
                          style={{
                            background:
                              "linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('assets/image.png')",
                            backgroundPosition: "center center",
                            backgroundSize: "cover",
                          }}
                        >
                          <div className="card-header pt-7 mb-3">
                            <h3 className="card-title align-items-start flex-column">
                              <span className="card-label fw-bold text-white">
                                Overview and Reports
                              </span>
                            </h3>
                          </div>

                          <div className="card-body d-flex flex-column justify-content-between pb-5 px-0">
                            <ul className="adera-custom-tabs mb-3 mx-9">
                              {[
                                {
                                  id: "compliance",
                                  label: "Compliance Report",
                                },
                                {
                                  id: "top-agents",
                                  label: "Top Performing Agent",
                                },
                              ].map((tab) => (
                                <li className="adera-tab-item" key={tab.id}>
                                  <button
                                    className={`adera-tab-btn ${activeTab === tab.id ? "active" : ""}`}
                                    onClick={() => setActiveTab(tab.id)}
                                  >
                                    {tab.label}
                                  </button>
                                </li>
                              ))}
                            </ul>

                            <div className="tab-content ps-4 pe-6 mt-5">
                              {activeTab === "compliance" && (
                                <div className="tab-pane fade show active">
                                  <div className="row g-4">
                                    <div className="col-lg-6 mb-xl-4 d-flex">
                                      <div className="card glass-card bg-body-white bg-opacity-10 hoverable w-100 h-100 border-0 p-5 shadow-sm">
                                        <div className="fw-bold text-white fs-6 mb-3">
                                          Expected Revenue
                                        </div>

                                        <div className="d-flex align-items-center mt-auto">
                                          <span className="fs-2x fw-bold text-white me-2 lh-1">
                                            {loading ? (
                                              <div className="skeleton-box w-50 opacity-50"></div>
                                            ) : (
                                              formatCurrency(
                                                stats?.expectedRevenue?.total,
                                              )
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="col-lg-6 mb-xl-4 d-flex">
                                      <div className="card glass-card bg-body-white bg-opacity-10 hoverable w-100 h-100 border-0 p-5 shadow-sm">
                                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                          <div className="fw-bold text-white fs-6">
                                            Revenue
                                          </div>
                                          <div className="d-flex gap-1 flex-wrap">
                                            {[
                                              "Today",
                                              "Week",
                                              "Month",
                                              "Year",
                                            ].map((t) => (
                                              <span
                                                key={t}
                                                className={`filter-pill ${revenueFilter === t.toLowerCase() ? "active" : ""}`}
                                                onClick={() =>
                                                  setRevenueFilter(
                                                    t.toLowerCase(),
                                                  )
                                                }
                                              >
                                                {t}
                                              </span>
                                            ))}
                                          </div>
                                        </div>

                                        <div className="d-flex align-items-center mt-auto">
                                          <span className="fs-2x fw-bold text-white me-2 lh-1">
                                            {loading ? (
                                              <div className="skeleton-box w-50 opacity-50"></div>
                                            ) : (
                                              formatCurrency(
                                                getFilteredCollectedAmount(),
                                              )
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="col-12">
                                    <div className="card bg-white p-6 shadow-sm mt-4">
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <div className="fw-bold text-gray-800 fs-4">
                                          Expected Revenue Target
                                        </div>
                                        <span className="fs-2x fw-bold text-warning">
                                          {loading
                                            ? "..."
                                            : expectedRevenueTargetPercentage}
                                          %
                                        </span>
                                      </div>
                                      <div className="progress h-10px bg-light">
                                        <div
                                          className="progress-bar bg-warning"
                                          role="progressbar"
                                          style={{
                                            width: `${Math.min(expectedRevenueTargetPercentage, 100)}%`,
                                          }}
                                        ></div>
                                      </div>
                                      <div className="d-flex justify-content-between text-semibold fs-7 mt-3">
                                        <span>
                                          Current:{" "}
                                          {formatCurrency(expectedRevenueTotal)}
                                        </span>
                                        <span>
                                          Overall Target:{" "}
                                          {formatCurrency(REVENUE_TARGET)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {activeTab === "top-agents" && (
                                <div className="tab-pane fade show active">
                                  <div className="card bg-white p-8 shadow-sm">
                                    <div className="d-flex align-items-center gap-5 mb-7">
                                      <div className="symbol symbol-circle symbol-90px">
                                        {loading ? (
                                          <div className="skeleton-circle"></div>
                                        ) : (
                                          <img
                                            src={getAgentImage()}
                                            alt="Agent"
                                            className="object-fit-cover rounded"
                                            onError={(e) => {
                                              e.currentTarget.src =
                                                "assets/edsal.png";
                                            }}
                                          />
                                        )}
                                      </div>
                                      <div>
                                        <div className="fw-bold fs-3 text-gray-900">
                                          {loading ? (
                                            <div className="skeleton-box w-100px mb-1"></div>
                                          ) : (
                                            getAgentName()
                                          )}
                                        </div>
                                        <div className="text-muted fs-8 mt-1">
                                          ID: {stats?.topAgent?.id || "N/A"}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="row g-4">
                                      <div className="col-6">
                                        <div className="border rounded p-4 text-center">
                                          <div className="text-muted fs-8 mb-1">
                                            Signed Clients
                                          </div>
                                          <div className="fw-bold fs-2 text-gray-900">
                                            {loading
                                              ? "..."
                                              : getAgentSignedClients()}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-6">
                                        <div className="border rounded p-4 text-center">
                                          <div className="text-muted fs-8 mb-1">
                                            Amount Collected
                                          </div>
                                          <div className="fw-bold fs-2 text-success">
                                            {loading
                                              ? "..."
                                              : formatCurrency(
                                                  getAgentTotalAmount(),
                                                )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="mt-6">
                                      <Link
                                        to="/ranking"
                                        className="btn btn-light-primary w-100 fw-semibold"
                                      >
                                        <i className="ki-outline ki-arrow-right me-2"></i>
                                        View All Agent Rankings
                                      </Link>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-7">
                        <div className="card card-flush h-xl-100">
                          <div className="card-header pt-7 mb-3">
                            <h3 className="card-title align-items-start flex-column">
                              <span className="card-label fw-bold">
                                Insights
                              </span>
                            </h3>
                            <div className="card-toolbar">
                              <div className="d-flex align-items-center gap-2">
                                <select
                                  className="form-select  w-auto"
                                  value={dateRange}
                                  onChange={(e) => setDateRange(e.target.value)}
                                  style={{ minWidth: "120px" }}
                                >
                                  <option value="all">All Time</option>
                                  <option value="7d">Last 7 Days</option>
                                  <option value="30d">Last 30 Days</option>
                                  <option value="90d">Last 90 Days</option>
                                  <option value="custom">Custom Range</option>
                                </select>

                                {dateRange === "custom" && (
                                  <div className="d-flex gap-2">
                                    <input
                                      type="date"
                                      className="form-control "
                                      value={customStartDate}
                                      onChange={(e) =>
                                        setCustomStartDate(e.target.value)
                                      }
                                      style={{ width: "140px" }}
                                    />
                                    <span className="align-self-center">
                                      to
                                    </span>
                                    <input
                                      type="date"
                                      className="form-control "
                                      value={customEndDate}
                                      onChange={(e) =>
                                        setCustomEndDate(e.target.value)
                                      }
                                      style={{ width: "140px" }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="card-body d-flex flex-column justify-content-between pb-5">
                            <ul className="adera-custom-tabs dark-tabs mb-3 mx-2">
                              {[
                                { id: "revenue-trend", label: "Revenue Trend" },
                                { id: "trend", label: "Enumeration Trend" },
                                {
                                  id: "distribution",
                                  label: "Ad Type Distribution",
                                },
                              ].map((tab) => (
                                <li className="adera-tab-item" key={tab.id}>
                                  <button
                                    className={`adera-tab-btn ${insightsTab === tab.id ? "active" : ""}`}
                                    onClick={() => setInsightsTab(tab.id)}
                                  >
                                    {tab.label}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div
                            className="p-6 mx-3 rounded"
                            style={{ height: "350px" }}
                          >
                            {chartsLoading ? (
                              <div className="w-100 h-100">
                                <div className="skeleton-chart"></div>
                              </div>
                            ) : (
                              <>
                                {insightsTab === "revenue-trend" && (
                                  <div className="h-100 w-100">
                                    {processedRevenueTrend.labels.length > 0 ? (
                                      <Line
                                        data={revenueLineChartData}
                                        options={lineChartOptions}
                                      />
                                    ) : (
                                      <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                                        No revenue data available for selected
                                        date range
                                      </div>
                                    )}
                                  </div>
                                )}

                                {insightsTab === "trend" && (
                                  <div className="h-100 w-100">
                                    {trendData.labels.length > 0 ? (
                                      <Line
                                        data={enumerationLineChartData}
                                        options={lineChartOptions}
                                      />
                                    ) : (
                                      <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                                        No data available for selected date
                                        range
                                      </div>
                                    )}
                                  </div>
                                )}

                                {insightsTab === "distribution" && (
                                  <div className="h-100 w-100">
                                    {adTypeDistribution.length > 0 ? (
                                      <Bar
                                        data={barChartData}
                                        options={barChartOptions}
                                      />
                                    ) : (
                                      <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                                        No ad type data available for selected
                                        date range
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="app-footer py-10 mt-10 border-top">
                      <div className="text-center">
                        <span className="text-muted fw-semibold me-1">
                          2026©
                        </span>
                        <a
                          href="https://edsaa.com.ng/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-800 text-hover-primary fw-bold"
                        >
                          EDSAA - ADERA
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboardaa;
